import { SegmentCapacitorPlugin } from "capacitor-segment-plugin";
import { Deploy } from "cordova-plugin-ionic";

import { getSharedInitId } from "../../../../app/utils/analytics";
import {
  isCapacitorPlatform,
  isIosPlatform,
} from "../../../../appV2/lib/utils/capacitor/platformUtils";

interface MiddlewarePayload {
  obj: {
    context: { page: { url: string; search: string; referrer: string }; initId?: string };
  };
}

interface SourceMiddlewareParams {
  payload: MiddlewarePayload;
  next: (payload: MiddlewarePayload, integrations: unknown) => void;
  integrations: unknown;
}

class SegmentPluginHelper {
  isInitialized: boolean;
  otaBuildId: string | undefined;
  initId: string;

  constructor() {
    this.isInitialized = false;
    this.otaBuildId = undefined;
    this.initId = getSharedInitId();
    this.createSegmentInstance = this.createSegmentInstance.bind(this);
  }

  async createSegmentInstance(segmentKeys: Record<string, string>): Promise<void> {
    if (this.isInitialized) {
      return;
    } else {
      const { iosKey, androidKey, webKey } = segmentKeys || {};
      if (isCapacitorPlatform()) {
        // Do not initialize when already an instance exists.
        const { instanceExist } = await SegmentCapacitorPlugin.isInstanceExist();
        if (instanceExist) {
          return;
        }
        const currentOtaVersion = await Deploy.getCurrentVersion();
        this.otaBuildId = currentOtaVersion?.buildId;
        const key = isIosPlatform() ? iosKey : androidKey;
        if (this.isInitialized) {
          return;
        }
        SegmentCapacitorPlugin.setUp({
          key,
          trackLifecycle: true,
        });
        this.isInitialized = true;
      } else {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore Element implicitly has an 'any' type because index expression is not of type 'number'.
        const analytics: any = (window["analytics"] = window["analytics"] || []);
        if (this.isInitialized) {
          return;
        }
        if (!analytics.initialize) {
          if (analytics.invoked) {
            // FIXME: Automatically disabled during ESLint config update
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            window.console && console.error && console.error("Segment snippet included twice.");
          } else {
            analytics.invoked = !0;
            analytics.methods = [
              "trackSubmit",
              "trackClick",
              "trackLink",
              "trackForm",
              "pageview",
              "identify",
              "reset",
              "group",
              "track",
              "ready",
              "alias",
              "debug",
              "page",
              "once",
              "off",
              "on",
              "addSourceMiddleware",
              "addIntegrationMiddleware",
              "setAnonymousId",
              "addDestinationMiddleware",
            ];
            analytics.factory = function (t: unknown) {
              return function (...args: unknown[]) {
                args.unshift(t);
                analytics.push(args);
                return analytics;
              };
            };
            for (let t = 0; t < analytics.methods.length; t++) {
              const e = analytics.methods[t];
              analytics[e] = analytics.factory(e);
            }
            analytics.load = function (t: string, e: Record<string, unknown>) {
              const n = document.createElement("script");
              n.type = "text/javascript";
              n.async = !0;
              n.src = "https://cdn.segment.com/analytics.js/v1/" + t + "/analytics.min.js";
              const a = document.getElementsByTagName("script")[0]!;
              a.parentNode?.insertBefore(n, a);
              analytics._loadOptions = e;
            };
            analytics.SNIPPET_VERSION = "4.1.0";
            analytics.load(webKey);

            // Capture initId for use in middleware
            const { initId } = this;

            // filter out sensitive data from the context sent to Segment and add initId
            analytics.addSourceMiddleware(
              ({ payload, next, integrations }: SourceMiddlewareParams) => {
                try {
                  // Add initId to context
                  if (payload.obj?.context) {
                    payload.obj.context.initId = initId;
                  }

                  const url = payload.obj?.context?.page?.url;
                  if (url) {
                    payload.obj.context.page.url = url.replace(/&?oobCode=\w+/g, "");
                  }

                  const search = payload.obj?.context?.page?.search;
                  if (search) {
                    payload.obj.context.page.search = search.replace(/&?oobCode=\w+/g, "");
                  }

                  const referrer = payload.obj?.context?.page?.referrer;
                  if (referrer) {
                    payload.obj.context.page.referrer = referrer.replace(/&?oobCode=\w+/g, "");
                  }
                  next(payload, integrations);
                  // FIXME: Automatically disabled during ESLint config update
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                } catch (error) {
                  next(payload, integrations);
                }
              }
            );
          }
        }
        this.isInitialized = true;
      }
    }
  }

  async capacitorTrack(
    eventName: string,
    properties?: {
      [K: string]: any;
    }
  ): Promise<void> {
    if (isCapacitorPlatform()) {
      await SegmentCapacitorPlugin.track({
        eventName,
        properties,
        options: { context: { otaBuildId: this.otaBuildId, initId: this.initId } },
      });
    }
  }

  webTrack(
    eventName: string,
    properties?: {
      [K: string]: any;
    }
  ): void {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore Element implicitly has an 'any' type because index expression is not of type 'number'.
    window["analytics"].track(eventName, { ...properties });
  }

  async capacitorIdentify(userID: string, traits: Record<string, unknown>): Promise<void> {
    if (isCapacitorPlatform()) {
      await SegmentCapacitorPlugin.identify({ userID, traits });
    }
  }

  webIdentify(userId: string, payload: Record<string, unknown>): void {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore Element implicitly has an 'any' type because index expression is not of type 'number'.
    window["analytics"].identify(userId, payload, {
      integrations: {},
    });
  }
}

const segmentPluginHelper = new SegmentPluginHelper();

export { segmentPluginHelper };
